import { createPortal } from 'react-dom';
import classnames from 'classnames';
import { Autocomplete } from '@entegral/components.autocomplete';
import { Dropdown } from '@entegral/components.dropdown';
import { CheckboxList } from '@entegral/components.checkbox-list';
import { Switcher } from '../abstract/switcher';

export function Filters({
  config,

  isOpened,
  close,

  status,
  statusOptions,
  onStatusSelect,

  areas,
  areaQuery,
  areaMatches,
  onAreaQueryChange,
  onAreaSelect,
  onAreaRemove,

  typeOptions,
  typeCaptionFormatter,
  onTypeToggle,

  priceMin,
  priceMinOptions,
  onPriceMinSelect,

  priceMax,
  priceMaxOptions,
  onPriceMaxSelect,

  bedroom,
  bedroomOptions,
  onBedroomSelect,

  bathroom,
  bathroomOptions,
  onBathroomSelect,

  garage,
  garageOptions,
  onGarageSelect,

  parking,
  parkingOptions,
  onParkingSelect,

  floorSize,
  floorSizeOptions,
  onFloorSizeSelect,

  maxFloorSize,
  maxFloorSizeOptions,
  onMaxFloorSizeSelect,

  landSize,
  landSizeOptions,
  onLandSizeSelect,

  pool,
  onPoolChange,

  pets,
  onPetsChange,

  flatlet,
  onFlatletChange,

  staffRooms,
  onStaffRoomsChange,

  development,
  onDevelopmentChange,

  onShow,
  onOnShowChange,

  reduced,
  onReducedChange,

  furnished,
  onFurnishedChange,

  features,
  featuresOptions,
  onFeatureToggle,
  onFeatureChange,
}) {
  let filtersList = new Set(config.additionalFilters || ['minPrice', 'maxPrice', 'beds', 'baths', 'garages', 'landSize', 'buildingSize', 'pool', 'pets']);

  return createPortal((
    <div className={classnames(`popup`, { [`popup_state_opened`]: isOpened })}>
      <div className={`popup__backdrop`} onClick={close}></div>

      <div className={`popup__dialog`}>
        <div className={`popup__heading`}>
          Refine your search
        </div>

        <div className={`popup__body`}>
          <Autocomplete
            placeholder={`Enter any location`}
            autoComplete={`property-search-bar`}
            query={areaQuery}
            matches={areaMatches}
            onQueryChange={onAreaQueryChange}
            onMatchSelect={onAreaSelect}
          />

          {!!areas.length && (
            <div className={`popup__areas`}>
              {areas.map(x => (
                <div
                  key={x.id}
                  className={`popup__area button button_size_small`}
                  onClick={() => onAreaRemove(x)}
                >
                  {x.term}
                </div>
              ))}
            </div>
          )}

          <div className={`popup__dropdowns`}>
            <Dropdown
              placeholder={`Status`}
              selectedOption={status}
              options={statusOptions}
              onSelect={onStatusSelect}
            />

            <CheckboxList
              options={typeOptions}
              onToggleItem={onTypeToggle}
              captionFormatter={typeCaptionFormatter}
            />

            {
              filtersList.has('minPrice') && (
                <Dropdown
                  placeholder={`Min Price`}
                  prefix={`From `}
                  selectedOption={priceMin}
                  options={priceMinOptions}
                  onSelect={onPriceMinSelect}
                />
              )
            }

            {
              filtersList.has('maxPrice') && (
                <Dropdown
                  placeholder={`Max Price`}
                  prefix={`Up to `}
                  selectedOption={priceMax}
                  options={priceMaxOptions}
                  onSelect={onPriceMaxSelect}
                />
              )
            }

            {
              filtersList.has('beds') && (
                <Dropdown
                  placeholder={`Beds`}
                  postfix={` beds`}
                  selectedOption={bedroom}
                  options={bedroomOptions}
                  onSelect={onBedroomSelect}
                />
              )
            }

            {
              filtersList.has('baths') && (
                <Dropdown
                  placeholder={`Baths`}
                  postfix={` baths`}
                  selectedOption={bathroom}
                  options={bathroomOptions}
                  onSelect={onBathroomSelect}
                />
              )
            }

            {
              filtersList.has('garages') && (
                <Dropdown
                  placeholder={`Garages`}
                  postfix={` garages`}
                  selectedOption={garage}
                  options={garageOptions}
                  onSelect={onGarageSelect}
                />
              )
            }

            {
              filtersList.has('parking') && (
                <Dropdown
                  placeholder={`Parking`}
                  postfix={` parking bays`}
                  selectedOption={parking}
                  options={parkingOptions}
                  onSelect={onParkingSelect}
                />
              )
            }

            {
              filtersList.has('landSize') && (
                <Dropdown
                  placeholder={`Land Size`}
                  prefix={`Land `}
                  selectedOption={landSize}
                  options={landSizeOptions}
                  onSelect={onLandSizeSelect}
                />
              )
            }

            {
              filtersList.has('buildingSize') && (
                <Dropdown
                  placeholder={`Floor Size`}
                  prefix={`Floor `}
                  selectedOption={floorSize}
                  options={floorSizeOptions}
                  onSelect={onFloorSizeSelect}
                />
              )
            }

            {
              filtersList.has('maxBuildingSize') && (
                <Dropdown
                  placeholder={`Max Floor Size`}
                  prefix={`Max Floor `}
                  selectedOption={maxFloorSize}
                  options={maxFloorSizeOptions}
                  onSelect={onMaxFloorSizeSelect}
                />
              )
            }

            {
              config.featureOptions?.multiBinary?.map(x => (
                <CheckboxList
                  key={x.name}
                  placeholder={x.name}
                  options={featuresOptions[x.name]}
                  onToggleItem={onFeatureToggle}
                  nonePlaceholder={x.name}
                  allPlaceholder={x.name}
                />
              ))
            }
          </div>

          <div className={`popup__switchers`}>
            {
              filtersList.has('pool') && (
                <Switcher
                  checked={pool}
                  label={`Pool`}
                  onChange={onPoolChange}
                />
              )
            }

            {
              filtersList.has('pets') && (
                <Switcher
                  checked={pets}
                  label={`Pet Friendly`}
                  onChange={onPetsChange}
                />
              )
            }

            {
              filtersList.has('flatlet') && (
                <Switcher
                  checked={flatlet}
                  label={`Flatlet`}
                  onChange={onFlatletChange}
                />
              )
            }

            {
              filtersList.has('staffRooms') && (
                <Switcher
                  checked={staffRooms}
                  label={`Staff Rooms`}
                  onChange={onStaffRoomsChange}
                />
              )
            }

            {
              filtersList.has('development') && (
                <Switcher
                  checked={development}
                  label={`Development`}
                  onChange={onDevelopmentChange}
                />
              )
            }

            {
              filtersList.has('onShow') && (
                <Switcher
                  checked={onShow}
                  label={`On Show`}
                  onChange={onOnShowChange}
                />
              )
            }

            {
              filtersList.has('reduced') && (
                <Switcher
                  checked={reduced}
                  label={`Reduced`}
                  onChange={onReducedChange}
                />
              )
            }

            {
              filtersList.has('furnished') && (
                <Switcher
                  checked={furnished}
                  label={`Furnished`}
                  onChange={onFurnishedChange}
                />
              )
            }

            {config.featureOptions?.binary?.map(x => (
              <Switcher
                key={x.value}
                name={x.value}
                checked={features[x.value]}
                label={x.name || x.value}
                onChange={onFeatureChange}
              />
            ))}
          </div>
        </div>

        <div className={`popup__actions`}>
          <div className={`popup__action button button_size_small`} onClick={close}>
            Show results
          </div>
        </div>
      </div>
    </div>
  ), document.body);
}
